import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { findReadingTypeById } from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import envConfig from '../../../envConfig';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import { CrudType } from '../../../utility/types/CrudType';
import { mapToThresholdRange } from '../../../classes/mappers/JsonMappers';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import { selectSessionInformation } from '../sessionSlice';

const selectAllThresholdRanges = createSelector([
  (state) => state.thresholdRange],
(thresholdRange) => thresholdRange.thresholdRangeList.map(
  (range) => mapToThresholdRange(range),
));

const selectThresholdRangeById = createSelector([
  (state) => state.thresholdRange.thresholdRangeList,
  (rangeList, thresholdRangeId) => thresholdRangeId,
],
(thresholdRangeList, id) => {
  const thresholdRange = thresholdRangeList.find((threshold) => threshold.id === id);
  return thresholdRange ? mapToThresholdRange(thresholdRange) : null;
});

const selectThresholdCrudOperation = createSelector(
  (state) => state.thresholdRange,
  (slice) => slice.crudOperation,
);

const selectThresholdCrudStatus = createSelector(
  (state) => state.thresholdRange,
  (slice) => slice.crudStatus,
);

const selectThresholdRangeErrors = createSelector(
  (state) => state.thresholdRange,
  (slice) => slice.errors,
);

const selectThresholdRangesLoading = createSelector(
  (state) => state.thresholdRange.loading,
  (loading) => loading,
);

const fetchThresholds = createAsyncThunk('securedApi/thresholdRange/fetchAll', async (authToken, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const sessionInfo = selectSessionInformation(state);
    const options = { temperatureScale: sessionInfo ? sessionInfo.temperatureScale : 'C' };

    const response = await RaaAuthenticatedRemoteService.globalInstance().get(`${envConfig.apiUrl}/api/v1/threshold-ranges`);

    return (response.thresholdRangeList || []).map((range) => {
      const readingTypeMeta = findReadingTypeById(range.readingType);
      return {
        ...range,
        value: readingTypeMeta.toUserDisplayValue(range.value, options),
        altValue: range.altValue !== null
          ? readingTypeMeta.toUserDisplayValue(range.altValue, options) : null,
      };
    });
  } catch (error) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

const fetchThresholdRangeById = createAsyncThunk('securedApi/thresholdRange/fetchById', async (thresholdRangeId, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const sessionInfo = selectSessionInformation(state);
    const options = { temperatureScale: sessionInfo ? sessionInfo.temperatureScale : 'C' };

    const response = await RaaAuthenticatedRemoteService.globalInstance().get(`${envConfig.apiUrl}/api/v1/threshold-ranges/${thresholdRangeId}`);
    const { thresholdRange } = response;
    const readingTypeMeta = findReadingTypeById(thresholdRange.readingType);

    return {
      ...thresholdRange,
      value: readingTypeMeta.toUserDisplayValue(thresholdRange.value, options),
      altValue: thresholdRange.altValue !== null
        ? readingTypeMeta.toUserDisplayValue(thresholdRange.altValue, options)
        : null,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const createThresholdRange = createAsyncThunk('securedApi/thresholdRange/create', async (thresholdRange, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const sessionInfo = selectSessionInformation(state);
    const options = { temperatureScale: sessionInfo ? sessionInfo.temperatureScale : 'C' };
    const readingTypeMeta = findReadingTypeById(thresholdRange.readingType);

    const apiPayload = {
      ...thresholdRange,
      value: readingTypeMeta.fromUserDisplayValue(thresholdRange.value, options),
      altValue: thresholdRange.altValue !== null && thresholdRange.altValue !== ''
        ? readingTypeMeta.fromUserDisplayValue(thresholdRange.altValue, options)
        : null,
    };

    const response = await RaaAuthenticatedRemoteService.globalInstance().post(`${envConfig.apiUrl}/api/v1/threshold-ranges`,
      { data: apiPayload });

    const newRange = response.thresholdRange;

    return {
      ...newRange,
      value: readingTypeMeta.toUserDisplayValue(newRange.value, options),
      altValue: newRange.altValue !== null
        ? readingTypeMeta.toUserDisplayValue(newRange.altValue, options)
        : null,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const deleteThresholdRange = createAsyncThunk('securedApi/thresholdRange/delete', async (thresholdRangeId, thunkAPI) => {
  try {
    await RaaAuthenticatedRemoteService.globalInstance().delete(`${envConfig.apiUrl}/api/v1/threshold-ranges/${thresholdRangeId}`);
    return thunkAPI.fulfillWithValue(thresholdRangeId);
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const updateThresholdRange = createAsyncThunk('securedApi/thresholdRange/update', async (thresholdRange, thunkAPI) => {
  try {
    const state = thunkAPI.getState();
    const sessionInfo = selectSessionInformation(state);
    const options = { temperatureScale: sessionInfo ? sessionInfo.temperatureScale : 'C' };
    const readingTypeMeta = findReadingTypeById(thresholdRange.readingType);

    const apiPayload = {
      ...thresholdRange,
      value: readingTypeMeta.fromUserDisplayValue(thresholdRange.value, options),
      altValue: thresholdRange.altValue !== null && thresholdRange.altValue !== ''
        ? readingTypeMeta.fromUserDisplayValue(thresholdRange.altValue, options)
        : null,
      patchFields: Object.keys(thresholdRange),
    };

    await RaaAuthenticatedRemoteService.globalInstance().patch(`${envConfig.apiUrl}/api/v1/threshold-ranges/${thresholdRange.id}`,
      { data: apiPayload });

    return thresholdRange;
  } catch (error) {
    return thunkAPI.rejectWithValue({
      name: error.name,
      message: error.message,
      data: error.data,
    });
  }
});

const initialState = {
  loading: true,
  loadingError: false,
  thresholdRangeList: [],
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  errors: [],
};

const thresholdRangeSlice = createSlice({
  name: 'thresholdRange',
  initialState,
  reducers: {
    clearThresholdRangeSlice: () => initialState,
    setThresholdRangeCrudStatus: (state, action) => { state.crudStatus = action.payload; },
    removeThresholdRange: (state, action) => {
      state.thresholdRangeList = [...state.thresholdRangeList]
        .filter((range) => range.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThresholds.pending, () => {
      })
      .addCase(fetchThresholds.fulfilled, (state, action) => {
        state.thresholdRangeList = action.payload;
        state.loading = false;
      })
      .addCase(fetchThresholds.rejected, (state) => {
        state.loading = false;
        state.loadingError = true;
      })
      .addCase(fetchThresholdRangeById.fulfilled, (state, action) => {
        const index = state.thresholdRangeList.findIndex(
          (tr) => tr.id === action.payload.id,
        );

        if (index !== -1) {
          state.thresholdRangeList[index] = action.payload;
        }
      })
      .addCase(fetchThresholdRangeById.rejected, (state, action) => {
        state.errors = action.payload;
      })
      .addCase(createThresholdRange.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.CREATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(createThresholdRange.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        const newThresholdRange = action.payload;
        const thresholdRangeCopy = Object.assign(
          Object.create(Object.getPrototypeOf(newThresholdRange)), newThresholdRange,
        );
        state.thresholdRangeList = [...state.thresholdRangeList, thresholdRangeCopy];
      })
      .addCase(createThresholdRange.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = [...action.payload.data];
      })
      .addCase(updateThresholdRange.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.UPDATE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(updateThresholdRange.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        const newThresholdRange = action.payload;
        const updatedThresholdRanges = [...state.thresholdRangeList]
          .filter((range) => range.id !== newThresholdRange.id);
        updatedThresholdRanges.push(newThresholdRange);
        state.thresholdRangeList = [...updatedThresholdRanges];
      })
      .addCase(updateThresholdRange.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(deleteThresholdRange.pending, (state) => {
        state.errors = [];
        state.crudOperation = CrudType.DELETE;
        state.crudStatus = CrudStatus.PENDING;
      })
      .addCase(deleteThresholdRange.fulfilled, (state, action) => {
        state.crudStatus = CrudStatus.SUCCESS;
        const thresholdId = action.payload;
        state.thresholdRangeList = state
          .thresholdRangeList.filter((range) => range.id !== thresholdId);
      })
      .addCase(deleteThresholdRange.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      });
  },
});

const { actions, reducer } = thresholdRangeSlice;

export const {
  clearThresholdRangeSlice,
  setThresholdRangeCrudStatus, removeThresholdRange,
} = actions;
export {
  selectThresholdRangesLoading,
  selectAllThresholdRanges,
  selectThresholdRangeById,
  createThresholdRange,
  updateThresholdRange,
  deleteThresholdRange,
  selectThresholdCrudOperation,
  selectThresholdCrudStatus,
  fetchThresholds,
  selectThresholdRangeErrors,
  fetchThresholdRangeById,
};
export default reducer;
